import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './ReportStatePanel.module.css';

export type ReportStateIcon = 'warning' | 'info' | 'alert';

/**
 * `default` — full-page centered card (not-found / error / empty pages).
 * `compact` — inline treatment for embedding inside a section card: no icon
 * chip, smaller min-height and title. Default is unchanged so existing
 * whole-page consumers are untouched.
 */
export type ReportStateVariant = 'default' | 'compact';

interface IReportStatePanelProps {
  icon: ReportStateIcon;
  titleKey: string;
  descriptionKey?: string;
  /** CTA — caller passes the retry button or back link. */
  action?: ReactNode;
  /** ARIA role override. Defaults: alert → "alert", warning/info → "status". */
  role?: 'status' | 'alert';
  variant?: ReportStateVariant;
}

/**
 * Centered card chrome for the not-found / error / empty branches of the
 * analytics report pages. Caller passes the i18n keys; this component owns
 * the visual + ARIA wiring. PageLoader is intentionally NOT routed through
 * here — see the design spec for why.
 */
export function ReportStatePanel({
  icon,
  titleKey,
  descriptionKey,
  action,
  role,
  variant = 'default',
}: IReportStatePanelProps) {
  const { t } = useTranslation();
  const effectiveRole = role ?? (icon === 'alert' ? 'alert' : 'status');
  const isCompact = variant === 'compact';
  return (
    <div
      className={`${classes.root} ${isCompact ? classes.compact : ''}`}
      role={effectiveRole}
    >
      {!isCompact && (
        <span className={`${classes.iconChip} ${iconClass(icon)}`} aria-hidden="true">
          <StateIcon icon={icon} />
        </span>
      )}
      <h2 className={classes.title}>{t(titleKey)}</h2>
      {descriptionKey && (
        <p className={classes.description}>{t(descriptionKey)}</p>
      )}
      {action && <div className={classes.action}>{action}</div>}
    </div>
  );
}

function iconClass(icon: ReportStateIcon): string {
  switch (icon) {
    case 'warning': return classes.iconWarning!;
    case 'info':    return classes.iconInfo!;
    case 'alert':   return classes.iconAlert!;
  }
}

/**
 * Inline SVG glyphs for the three state variants. Shapes are vendored
 * from Trusstor's report assets (warningCircle pattern) with `fill` /
 * `stroke` normalised to `currentColor` so the iconClass(...) variant
 * controls the colour.
 */
function StateIcon({ icon }: { icon: ReportStateIcon }) {
  if (icon === 'alert') {
    return (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6.13 14.63L10.51 5.88c.62-1.23 2.37-1.23 2.98 0l4.38 8.75c.56 1.11-.25 2.41-1.49 2.41H7.62c-1.24 0-2.05-1.31-1.49-2.41Z"
          fill="currentColor"
        />
        <path d="M12 9.33V11" stroke="white" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="14.33" r="0.5" fill="white" stroke="white" />
      </svg>
    );
  }
  if (icon === 'info') {
    return (
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        <path d="M12 11v5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="12" cy="8" r="1" fill="currentColor" />
      </svg>
    );
  }
  // warning
  return (
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
      <path d="M12 7v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="16" r="1" fill="currentColor" />
    </svg>
  );
}
